import type { PreLoadedDoc } from '@/lib/templates/types';
import type { P2PDemoSession, P2PDemoView } from '@/lib/p2pAppeals';

export type DemoAgentStatus = 'complete' | 'active' | 'pending';
export type DemoAgentPhase = 'pre-auth' | 'post-op' | 'p2p';
export type DemoAgentSourceType =
  | 'case_evidence'
  | 'payer_policy'
  | 'ncci_rule'
  | 'authorization'
  | 'payer_response';

export interface DemoAgentStatusItem {
  id: string;
  label: string;
  status: DemoAgentStatus;
}

export interface DemoAgentCitation {
  id: string;
  label: string;
  sourceType: DemoAgentSourceType;
  excerpt?: string;
}

export interface DemoAskLinkContext {
  phase: DemoAgentPhase;
  patientLabel: string;
  procedureLabel: string;
  cptCodes: string[];
  evidenceCitations: DemoAgentCitation[];
  policyCitations: DemoAgentCitation[];
  missingEvidenceLabels: string[];
  nextAction: string;
}

export interface DemoAgentExperience {
  phase: DemoAgentPhase;
  statusItems: DemoAgentStatusItem[];
  askLinkContext: DemoAskLinkContext;
}

export type DemoAskLinkQuestion =
  | 'why_recommended'
  | 'what_evidence'
  | 'what_is_missing'
  | 'what_happens_next';

export interface DemoAskLinkAnswer {
  answer: string;
  citations: DemoAgentCitation[];
}

interface PreAuthDemoAgentInput {
  step: number;
  patientName: string;
  procedureName: string;
  cptCodes: string[];
  payerName: string;
  documents?: PreLoadedDoc[];
}

interface PostOpDemoAgentInput {
  patientName: string;
  procedureName: string;
  cptCodes: string[];
  authorizedCount: number;
  discoveryCount: number;
  hasDictation: boolean;
  coverageComplete: boolean;
  missingCoverageLabels?: string[];
}

function normalizeCodeList(codes: string[]): string[] {
  return Array.from(new Set(codes.map(code => String(code || '').trim()).filter(Boolean)));
}

function describeCodes(codes: string[]): string {
  const normalized = normalizeCodeList(codes);
  return normalized.length > 0 ? normalized.join(', ') : 'the selected procedure';
}

function sourceId(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'source';
}

function uniqueCitations(citations: DemoAgentCitation[]): DemoAgentCitation[] {
  const seen = new Set<string>();
  return citations.filter((citation) => {
    const key = `${citation.sourceType}:${citation.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function buildPreAuthDemoAgentExperience({
  step,
  patientName,
  procedureName,
  cptCodes,
  payerName,
  documents = [],
}: PreAuthDemoAgentInput): DemoAgentExperience {
  const verifiedDocuments = documents.filter(document => (
    document.status === 'verified' || Boolean(document.extracted_text?.trim())
  ));
  const evidenceCitations = verifiedDocuments.slice(0, 4).map((document, index) => ({
    id: document.source_path || `${sourceId(document.type)}-${index + 1}`,
    label: document.type,
    sourceType: 'case_evidence' as const,
    excerpt: document.extracted_text?.trim().slice(0, 180),
  }));
  const codes = normalizeCodeList(cptCodes);
  const policyCitations: DemoAgentCitation[] = [
    {
      id: `demo-policy-${sourceId(payerName)}`,
      label: `${payerName} presentation policy matrix`,
      sourceType: 'payer_policy',
      excerpt: `Synthetic policy fixture evaluated for ${procedureName} and CPT ${describeCodes(codes)}.`,
    },
  ];

  if (codes.length > 1) {
    policyCitations.push({
      id: `demo-ncci-${codes.join('-')}`,
      label: 'Demo NCCI pair-rule fixture',
      sourceType: 'ncci_rule',
      excerpt: `The selected CPT universe was checked for the simulated ${procedureName} case.`,
    });
  }

  if (step >= 4) {
    policyCitations.push({
      id: 'compiled-pre-auth-packet',
      label: 'Compiled authorization packet',
      sourceType: 'authorization',
      excerpt: `The deterministic demo compiler prepared CPT ${describeCodes(codes)} for surgeon review.`,
    });
  }

  const statusItems: DemoAgentStatusItem[] = [
    { id: 'evidence', label: 'Evidence prepared', status: 'complete' },
    { id: 'policy', label: 'Policy checked', status: 'complete' },
    {
      id: 'cpt-strategy',
      label: step >= 3 ? 'CPT strategy verified' : 'CPT strategy preparing',
      status: step >= 3 ? 'complete' : 'active',
    },
    {
      id: 'packet',
      label: step >= 4 ? 'Packet compiled' : 'Packet compilation queued',
      status: step >= 4 ? 'complete' : step >= 3 ? 'active' : 'pending',
    },
  ];

  return {
    phase: 'pre-auth',
    statusItems,
    askLinkContext: {
      phase: 'pre-auth',
      patientLabel: patientName,
      procedureLabel: procedureName,
      cptCodes: codes,
      evidenceCitations,
      policyCitations,
      missingEvidenceLabels: [],
      nextAction: step >= 4
        ? 'Review the compiled authorization, then sign and submit it.'
        : 'Confirm the selected procedure and CPT universe so LINK can compile the authorization packet.',
    },
  };
}

export function buildPostOpDemoAgentExperience({
  patientName,
  procedureName,
  cptCodes,
  authorizedCount,
  discoveryCount,
  hasDictation,
  coverageComplete,
  missingCoverageLabels = [],
}: PostOpDemoAgentInput): DemoAgentExperience {
  const codes = normalizeCodeList(cptCodes);
  const evidenceCitations: DemoAgentCitation[] = [
    {
      id: 'signed-authorization-bundle',
      label: 'Signed authorization bundle',
      sourceType: 'authorization',
      excerpt: `${authorizedCount} authorized CPT line${authorizedCount === 1 ? '' : 's'} loaded as the operative starting universe: ${describeCodes(codes)}.`,
    },
    {
      id: `operative-context-${sourceId(procedureName)}`,
      label: 'Operative case context',
      sourceType: 'case_evidence',
      excerpt: `${procedureName}${discoveryCount > 0 ? ` with ${discoveryCount} surgeon-selected intra-op discover${discoveryCount === 1 ? 'y' : 'ies'}` : ''}.`,
    },
  ];

  if (hasDictation) {
    evidenceCitations.push({
      id: 'current-operative-dictation',
      label: 'Current operative dictation',
      sourceType: 'case_evidence',
      excerpt: coverageComplete
        ? 'The synthetic dictation fixture covers the active CPT documentation requirements.'
        : 'The current dictation is being checked against the active CPT documentation requirements.',
    });
  }

  const policyCitations: DemoAgentCitation[] = [
    {
      id: `demo-post-op-ncci-${codes.join('-') || 'case'}`,
      label: 'Demo NCCI and procedure-pair fixture',
      sourceType: 'ncci_rule',
      excerpt: 'Intra-op discovery suggestions were ranked against the authorized procedure family and simulated pair rules.',
    },
  ];

  return {
    phase: 'post-op',
    statusItems: [
      { id: 'authorization', label: 'Authorization loaded', status: 'complete' },
      { id: 'discoveries', label: 'NCCI suggestions ranked', status: 'complete' },
      {
        id: 'coverage',
        label: coverageComplete ? 'CPT coverage verified' : hasDictation ? 'CPT coverage checking' : 'Dictation coverage ready',
        status: coverageComplete ? 'complete' : 'active',
      },
      {
        id: 'operative-note',
        label: coverageComplete ? 'Operative note ready' : 'Operative note queued',
        status: coverageComplete ? 'active' : 'pending',
      },
    ],
    askLinkContext: {
      phase: 'post-op',
      patientLabel: patientName,
      procedureLabel: procedureName,
      cptCodes: codes,
      evidenceCitations,
      policyCitations,
      missingEvidenceLabels: missingCoverageLabels,
      nextAction: coverageComplete
        ? 'Review the prepared operative note, then sign and route it.'
        : 'Generate or dictate the operative details. LINK will track CPT coverage while the surgeon remains the clinical authority.',
    },
  };
}

export function buildP2PDemoAgentExperience(
  session: P2PDemoSession,
  view: P2PDemoView,
): DemoAgentExperience {
  const { scenario } = view;
  const gateVerified = !['off', 'armed', 'payer_pending'].includes(session.stage)
    && Boolean(session.submittedAt)
    && Boolean(session.denialReceivedAt);
  const callPrepared = ['p2p_ready', 'appeal_ready', 'documentation_review_required', 'routed'].includes(session.stage);
  const appealPrepared = ['appeal_ready', 'routed'].includes(session.stage) && Boolean(view.appeal);
  const missingEvidenceLabels = view.evidence
    .filter(finding => finding.status === 'warning' || finding.status === 'blocked')
    .map(finding => finding.label);

  const evidenceCitations: DemoAgentCitation[] = scenario.evidencePayload.map(item => ({
    id: item.id,
    label: item.label,
    sourceType: 'case_evidence',
    excerpt: item.summary,
  }));
  evidenceCitations.push({
    id: `payer-response-${scenario.id}`,
    label: `${scenario.payerName} simulated denial response`,
    sourceType: 'payer_response',
    excerpt: `${scenario.denialCodes.join(' / ')} - ${scenario.denialRationale}`,
  });

  const policyCitations: DemoAgentCitation[] = [
    {
      id: `policy-${scenario.id}`,
      label: scenario.policyName,
      sourceType: 'payer_policy',
      excerpt: `Synthetic presentation policy fixture for ${scenario.payerName} / ${scenario.umVendor}.`,
    },
    {
      id: `submitted-authorization-${scenario.id}`,
      label: 'Frozen submitted authorization snapshot',
      sourceType: 'authorization',
      excerpt: `P2PGenie is restricted to the submitted CPT universe (${describeCodes(scenario.cptCodes)}) and its source-linked evidence.`,
    },
  ];

  return {
    phase: 'p2p',
    statusItems: [
      { id: 'denial', label: 'Denial classified', status: 'complete' },
      { id: 'gate', label: 'P2P gate verified', status: gateVerified ? 'complete' : 'pending' },
      { id: 'boundary', label: 'Submitted evidence bounded', status: gateVerified ? 'complete' : 'pending' },
      { id: 'strategy', label: 'Call strategy prepared', status: callPrepared ? 'complete' : 'active' },
      {
        id: 'appeal',
        label: appealPrepared ? 'Appeal draft prepared' : 'Appeal held until P2P outcome',
        status: appealPrepared ? 'complete' : 'pending',
      },
    ],
    askLinkContext: {
      phase: 'p2p',
      patientLabel: scenario.patientName,
      procedureLabel: scenario.procedureName,
      cptCodes: normalizeCodeList(scenario.cptCodes),
      evidenceCitations,
      policyCitations,
      missingEvidenceLabels,
      nextAction: appealPrepared
        ? 'Review the cited appeal draft and route it to LINK Ops for human-controlled submission.'
        : 'Use the prepared call protocol, record the simulated P2P outcome, and let LINK open the correct next pathway.',
    },
  };
}

export function answerDemoAskLinkQuestion(
  question: DemoAskLinkQuestion,
  context: DemoAskLinkContext,
): DemoAskLinkAnswer {
  const codes = describeCodes(context.cptCodes);
  const allCitations = uniqueCitations([...context.evidenceCitations, ...context.policyCitations]);

  if (question === 'what_evidence') {
    const sourceNames = context.evidenceCitations.map(citation => citation.label).slice(0, 4);
    return {
      answer: sourceNames.length > 0
        ? `LINK prepared this synthetic case from ${sourceNames.join(', ')}. Each displayed conclusion remains linked to the cited fixture below.`
        : 'No source evidence is displayed for this synthetic case yet.',
      citations: uniqueCitations(context.evidenceCitations),
    };
  }

  if (question === 'what_is_missing') {
    return {
      answer: context.missingEvidenceLabels.length > 0
        ? `The current synthetic record still calls for confirmation of: ${context.missingEvidenceLabels.join(', ')}. LINK can suggest language, but it cannot invent or confirm a clinical fact.`
        : 'No synthetic confirmation is outstanding in this presentation case. Production LINK would preserve any unresolved fact for clinician or RCM review.',
      citations: allCitations,
    };
  }

  if (question === 'what_happens_next') {
    return {
      answer: context.nextAction,
      citations: allCitations.slice(0, 4),
    };
  }

  if (context.phase === 'p2p') {
    return {
      answer: `P2PGenie opened only after the submitted authorization received a simulated payer denial. LINK matched CPT ${codes} to the frozen submitted evidence and the cited policy fixture; it cannot add new clinical facts or transmit an appeal without human review.`,
      citations: allCitations,
    };
  }

  if (context.phase === 'post-op') {
    return {
      answer: `LINK loaded CPT ${codes} from the signed authorization and ranked operative options against the synthetic procedure and NCCI fixtures. The surgeon still confirms what was actually performed before the note can be signed.`,
      citations: allCitations,
    };
  }

  return {
    answer: `LINK prepared CPT ${codes} from the cited synthetic case evidence and simulated payer rules for ${context.procedureLabel}. The recommendation is structured for review; the surgeon retains authority to confirm, sign, and send.`,
    citations: allCitations,
  };
}
